package dev.wyns.ventrova.ui.components

import androidx.compose.animation.animateColorAsState
import androidx.compose.animation.core.animateDpAsState
import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.runtime.staticCompositionLocalOf
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import dev.wyns.ventrova.app.StrictnessStop
import dev.wyns.ventrova.data.model.VeganLevel
import dev.wyns.ventrova.ui.theme.VentrovaColors

/** Controls that should use the platform's own widgets where Compose doesn't provide them. */
interface PlatformControls {
    @Composable
    fun Toggle(checked: Boolean, onCheckedChange: (Boolean) -> Unit, modifier: Modifier)

    @Composable
    fun SegmentedControl(options: List<String>, selectedIndex: Int, onSelect: (Int) -> Unit, modifier: Modifier)
}

/** Compose drawings of the design's switch and segmented control (used on web). */
open class DrawnPlatformControls(
    private val toggleOnColor: Color = VentrovaColors.SwitchOnIos,
    private val toggleWidth: Dp = 51.dp,
    private val toggleHeight: Dp = 31.dp,
    private val segmentHeight: Dp = 32.dp,
    private val segmentCorner: Dp = 9.dp,
) : PlatformControls {
    @Composable
    override fun Toggle(checked: Boolean, onCheckedChange: (Boolean) -> Unit, modifier: Modifier) {
        val track by animateColorAsState(if (checked) toggleOnColor else VentrovaColors.SwitchOff)
        val knob = toggleHeight - 4.dp
        val offset by animateDpAsState(if (checked) toggleWidth - knob - 4.dp else 0.dp)
        Box(
            modifier
                .size(toggleWidth, toggleHeight)
                .background(track, RoundedCornerShape(toggleHeight / 2))
                .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) { onCheckedChange(!checked) }
                .padding(2.dp),
        ) {
            Box(Modifier.offset(x = offset).size(knob).shadow(2.dp, CircleShape).background(Color.White, CircleShape))
        }
    }

    @Composable
    override fun SegmentedControl(options: List<String>, selectedIndex: Int, onSelect: (Int) -> Unit, modifier: Modifier) {
        Row(modifier.background(VentrovaColors.Ink.copy(alpha = 0.07f), RoundedCornerShape(segmentCorner + 3.dp)).padding(3.dp)) {
            options.forEachIndexed { index, option ->
                val active = index == selectedIndex
                Box(
                    Modifier
                        .weight(1f)
                        .height(segmentHeight)
                        .then(if (active) Modifier.shadow(2.dp, RoundedCornerShape(segmentCorner)).background(Color.White, RoundedCornerShape(segmentCorner)) else Modifier)
                        .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) { onSelect(index) },
                    contentAlignment = Alignment.Center,
                ) {
                    Text(option, style = bodyStyle(14.sp, if (active) FontWeight.SemiBold else FontWeight.Medium))
                }
            }
        }
    }
}

val LocalPlatformControls = staticCompositionLocalOf<PlatformControls> { DrawnPlatformControls() }

/** Level shown on each slider stop. */
val StrictnessStop.glyphLevel: VeganLevel
    get() = when (this) {
        StrictnessStop.VeganOptions -> VeganLevel.VeganOptions
        StrictnessStop.Vegetarian -> VeganLevel.Vegetarian
        StrictnessStop.Vegan -> VeganLevel.Vegan
    }

/**
 * The iOS/web "How plant-based?" slider: a thin track with a dark fill and three white knobs
 * carrying the level glyphs.
 */
@Composable
fun StrictnessSlider(
    selected: StrictnessStop?,
    onSelect: (StrictnessStop) -> Unit,
    modifier: Modifier = Modifier,
    knobSize: Dp = 34.dp,
    glyphSize: Dp = 16.dp,
    labelWidth: Dp = 76.dp,
    labelSize: Float = 12f,
) {
    val stops = StrictnessStop.entries
    val fraction by animateFloatAsState((selected?.ordinal ?: 0) / (stops.size - 1f))
    Column(modifier) {
        Box(Modifier.fillMaxWidth().height(knobSize)) {
            Canvas(Modifier.fillMaxWidth().height(knobSize)) {
                val left = knobSize.toPx() / 2
                val right = size.width - knobSize.toPx() / 2
                val top = size.height / 2 - 2.dp.toPx()
                val radius = CornerRadius(2.dp.toPx())
                drawRoundRect(VentrovaColors.Ink.copy(alpha = 0.1f), Offset(left, top), Size(right - left, 4.dp.toPx()), radius)
                drawRoundRect(VentrovaColors.Ink, Offset(left, top), Size((right - left) * fraction, 4.dp.toPx()), radius)
            }
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                stops.forEach { stop ->
                    Box(
                        Modifier
                            .size(knobSize)
                            .shadow(4.dp, CircleShape)
                            .background(Color.White, CircleShape)
                            .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) { onSelect(stop) },
                        contentAlignment = Alignment.Center,
                    ) {
                        LevelGlyph(stop.glyphLevel, size = glyphSize, borderWidth = 2.5.dp)
                    }
                }
            }
        }
        Row(Modifier.fillMaxWidth().padding(top = 4.dp), horizontalArrangement = Arrangement.SpaceBetween) {
            stops.forEach { stop ->
                val active = stop == selected
                // The outer labels align with the slider's edges so they never get clipped.
                Text(
                    stopLabel(stop),
                    style = bodyStyle(labelSize.sp, if (active) FontWeight.SemiBold else FontWeight.Medium, if (active) VentrovaColors.Ink else VentrovaColors.Subtle),
                    textAlign = when (stop) {
                        StrictnessStop.VeganOptions -> TextAlign.Start
                        StrictnessStop.Vegan -> TextAlign.End
                        else -> TextAlign.Center
                    },
                    maxLines = 1,
                    modifier = Modifier
                        .width(labelWidth)
                        .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) { onSelect(stop) },
                )
            }
        }
    }
}
